import * as React from 'react';
import { ThemeContext, type Theme, type ThemeContextValue, type ThemeSetting } from './context';

const THEME_STORAGE_KEY = 'theme';
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';

function getSystemTheme(): Theme {
    return window.matchMedia(SYSTEM_THEME_QUERY).matches ? 'dark' : 'light';
}

function subscribeSystemTheme(callback: () => void) {
    const media = window.matchMedia(SYSTEM_THEME_QUERY);
    media.addEventListener('change', callback);
    return () => media.removeEventListener('change', callback);
}

function applyTheme(theme: Theme) {
    document.documentElement.classList.toggle('dark', theme === 'dark');
}

function readInitialSetting(): ThemeSetting {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system')
        return raw;
    return 'system';
}

export default function ThemeProvider({ children }: {
    children: React.ReactNode;
}) {
    const [setting, setSetting] = React.useState<ThemeSetting>(() => {
        if (typeof window === 'undefined')
            return 'system';
        return readInitialSetting();
    });
    const systemTheme = React.useSyncExternalStore(subscribeSystemTheme, getSystemTheme);
    const theme = setting === 'system' ? systemTheme : setting;

    React.useEffect(() => {
        window.localStorage.setItem(THEME_STORAGE_KEY, setting);
    }, [setting]);

    React.useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    const toggle = React.useCallback(() => {
        setSetting((prev) => {
            const effective = prev === 'system' ? getSystemTheme() : prev;
            return effective === 'dark' ? 'light' : 'dark';
        });
    }, []);

    const value = React.useMemo<ThemeContextValue>(() => ({ setting, theme, setSetting, toggle }), [setting, theme, toggle]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
